import merge from 'deepmerge'
import { useCallback, useEffect } from 'react'

import { useChart } from '../contexts/ChartContext'
import { useExport } from '../contexts/ExportContext'
import Highcharts from '../highcharts'

import {
  changeSeriesType,
  resetOutliers,
  resetSeriesCustomizations,
} from './customizations'
import {
  addChartIdToClipboard,
  addImageToClipboard,
  downloadXLSX,
} from './export'
import { addSavedIndicator } from './indicators'

export const useIndicators = (onIndicatorsModalOpen: () => void) => {
  const { setOption } = useChart()

  const handleButtonClick = useCallback(() => {
    onIndicatorsModalOpen()
  }, [onIndicatorsModalOpen])

  useEffect(
    () =>
      setOption({
        navigation: {
          bindings: { jgpIndicators: { init: handleButtonClick } },
        },
      }),
    [setOption, handleButtonClick]
  )
}

export const useResetCustomizations = () => {
  const {
    chartRef,
    indicators,
    unregisterIndicator,
    setSeries,
    originalSeries,
    removePlotLine,
    setOption,
    resetTitle,
    seriesCustomizations,
    setSeriesCustomizations,
    setOutliers,
    defaultIndicators,
    addPlotLine,
  } = useChart()

  const handleButtonClick = useCallback(() => {
    setSeries(
      originalSeries.map(
        (series) => merge(series as any, { dataLabels: undefined }) as any
      )
    )

    indicators.forEach((indicator) => {
      if (indicator.type === 'plotline') {
        removePlotLine(indicator.id)
      }
      unregisterIndicator(indicator.id)
    })

    resetSeriesCustomizations({
      chartRef,
      setSeriesCustomizations,
    })
    resetTitle()
    resetOutliers({ setOutliers })

    defaultIndicators.forEach((indicator) => {
      if (indicator.type === 'plotline') {
        // Default plotlines do not get removed by the procedure above
        return
      }
      addSavedIndicator(indicator, { addPlotLine, setSeries })
    })
  }, [
    chartRef,
    indicators,
    unregisterIndicator,
    setSeries,
    removePlotLine,
    seriesCustomizations,
    setSeriesCustomizations,
  ])

  useEffect(() => {
    setOption({
      navigation: {
        bindings: { jgpReset: { init: handleButtonClick } },
      },
    })
  }, [setOption, handleButtonClick])
}

export const useChangeLineColumn = () => {
  const { setSeries, setOption, setSeriesCustomizations } = useChart()

  const changeLineToColumn = useCallback(
    (series: Highcharts.Series | undefined) => {
      if (series?.options.type !== 'line') {
        return
      }
      changeSeriesType(
        { setSeries, setSeriesCustomizations },
        series.options.id!,
        'column'
      )
    },
    [setSeries, setSeriesCustomizations]
  )

  const changeColumnToLine = useCallback(
    (series: Highcharts.Series | undefined) => {
      if (series?.options.type !== 'column') {
        return
      }
      changeSeriesType(
        { setSeries, setSeriesCustomizations },
        series.options.id!,
        'line'
      )
    },
    [setSeries, setSeriesCustomizations]
  )

  useEffect(() => {
    setOption({
      navigation: {
        bindings: {
          jgpChangeLineToColumn: {
            init: () => setOption({ tooltip: { enabled: false } }),
            start: (event: any) => {
              setOption({ tooltip: { enabled: true } })
              changeLineToColumn(event?.point?.series)
            },
          },
          jgpChangeColumnToLine: {
            init: () => setOption({ tooltip: { enabled: false } }),
            start: (event: any) => {
              setOption({ tooltip: { enabled: true } })
              changeColumnToLine(event?.point?.series)
            },
          },
        },
      },
    })
  }, [changeLineToColumn, changeColumnToLine, setOption])
}

export const useLegendSingleOut = () => {
  const DOUBLE_CLICK_INTERVAL_MS = 300

  const { registerEvent, setSeriesCustomizations } = useChart()

  const updateSeriesVisibility = useCallback(
    (seriesId: string, visible: boolean) =>
      setSeriesCustomizations((curCustomizations) =>
        merge(curCustomizations, {
          [seriesId]: {
            visible,
          },
        })
      ),
    [setSeriesCustomizations]
  )

  const recentClicks = new Set<string>()

  const handleLegendClick = useCallback(
    (
      series: Highcharts.Series & { legendItem: { element: HTMLElement } },
      event: Highcharts.SeriesLegendItemClickEventObject
    ) => {
      const seriesId = series.options.id

      if (!seriesId) {
        return
      }

      const singleOut = (id: string) => {
        event.preventDefault()
        series.chart.series.forEach((s) => {
          if (s.options.id === id) {
            s.options.id && updateSeriesVisibility(s.options.id, true)
            s.show()
          } else {
            s.options.id && updateSeriesVisibility(s.options.id, false)
            s.hide()
          }
        })
      }

      if (recentClicks.has(seriesId)) {
        singleOut(seriesId)
        recentClicks.delete(seriesId)
      } else {
        updateSeriesVisibility(seriesId, !series.visible)
        recentClicks.add(seriesId)
        setTimeout(() => {
          recentClicks.delete(seriesId)
        }, DOUBLE_CLICK_INTERVAL_MS)
      }
    },
    []
  )

  useEffect(() => {
    registerEvent(
      ['plotOptions', 'series', 'events', 'legendItemClick'],
      'legendSingleOut-onLegendItemClick',
      (ctx: any, event: any) => {
        handleLegendClick(ctx, event)
      }
    )
  }, [handleLegendClick])
}

export const useOutliers = () => {
  const { chartRef, setOption, outliers, setOutliers, setSeries } = useChart()

  const handleButtonClick = useCallback(() => {
    const points = chartRef.current?.chart.getSelectedPoints()

    if (!points) {
      return
    }

    setOutliers((curOutliers) =>
      curOutliers.concat(
        points.map((point) => ({
          seriesId: point.series.options.id!,
          x: point.options.x ?? point.options.name!,
          y: point.options.y!,
        }))
      )
    )
  }, [])

  useEffect(() => {
    setSeries((curSeries) =>
      curSeries.map((series) => {
        const data = (series as
          | Highcharts.SeriesLineOptions
          | Highcharts.SeriesColumnOptions).data as [number, number][]
        const seriesOutliers = outliers.filter(
          ({ seriesId }) => seriesId === series.id
        )
        const dataWithoutOutliers = data.filter((point: any) =>
          Array.isArray(point)
            ? !seriesOutliers.some(
                (outlier) => point[0] === outlier.x && point[1] === outlier.y
              )
            : !seriesOutliers.some(
                (outlier) => point.x === outlier.x && point.y === outlier.y
              )
        )

        return {
          ...series,
          data: dataWithoutOutliers,
        } as Highcharts.SeriesLineOptions | Highcharts.SeriesColumnOptions
      })
    )
  }, [outliers])

  useEffect(
    () =>
      setOption({
        navigation: {
          bindings: {
            jgpOutliers: {
              init: handleButtonClick,
            },
          },
        },
      }),
    [setOption, handleButtonClick]
  )
}

export const usePPTExport = () => {
  const { chartRef, containerRef, setOption, reportManagerId } = useChart()

  // A-size proportion
  const EXPORT_HEIGHT = 600
  const EXPORT_WIDTH = (600 / 210) * 297

  const context = useExport()

  useEffect(() => {
    if (!context) {
      console.error(
        'Chart components must be inside an ExportContextProvider in order to use exporting features'
      )
    } else {
      setOption({
        exporting: {
          buttons: {
            contextButton: {
              // Removes print button
              menuItems: [
                'viewFullscreen',
                'addToExportQueue',
                'addToClipboard',
                ...(reportManagerId ? ['addChartIdToClipboard'] : []),
                'separator',
                'downloadPNG',
                'downloadJPEG',
                'downloadPDF',
                'downloadSVG',
                'separator',
                'downloadCSV',
                'downloadXLSX',
              ],
            },
          },
          menuItemDefinitions: {
            addToExportQueue: {
              text: 'Adicionar à fila de exportação',
              onclick: () => {
                chartRef.current && context.addToQueue(chartRef.current.chart)
              },
            },
            addToClipboard: {
              text: 'Copiar para o clipboard',
              onclick: () => {
                containerRef.current &&
                  addImageToClipboard(containerRef.current).catch((err) =>
                    console.error(err)
                  )
              },
            },
            addChartIdToClipboard: {
              text: 'Copiar Chart Id para o clipboard',
              onclick: () => {
                reportManagerId && addChartIdToClipboard(reportManagerId)
              },
            },
            downloadXLSX: {
              textKey: 'downloadXLSX',
              onclick: downloadXLSX,
            },
          },
          chartOptions: {
            stockTools: {
              gui: {
                enabled: false,
              },
            },
            title: {
              style: {
                fontSize: '24px',
              },
            },
            rangeSelector: {
              enabled: false,
            },
          },
          csv: {
            decimalPoint: '.',
          },
          sourceHeight: EXPORT_HEIGHT,
          sourceWidth: EXPORT_WIDTH,
        },
      })
    }
  }, [context])
}

export const useDefaultIndicators = (indicators: Indicator[]) => {
  const { addPlotLine, setSeries } = useChart()

  useEffect(() => {
    indicators.forEach((indicator) =>
      addSavedIndicator(indicator, { addPlotLine, setSeries })
    )
  }, [])
}
